import React, { useEffect, useState } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from 'sonner';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import Home from '@/pages/Home';
import Shop from '@/pages/Shop';
import ProductDetail from '@/pages/ProductDetail';
import Cart from '@/pages/Cart';
import Checkout from '@/pages/Checkout';
import OrderConfirmation from '@/pages/OrderConfirmation';
import Admin from '@/pages/Admin';
import TankTailoredBundle from '@/pages/TankTailoredBundle';
import ShippingPolicy from '@/pages/ShippingPolicy';
import CurrentDeals from '@/pages/CurrentDeals';
import Blog from '@/pages/Blog';
import BlogPost from '@/pages/BlogPost';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import { MaintenancePage, ClosedForTodayPage } from '@/pages/SiteStatusPages';
import useCart from '@/store/cartStore';
import '@/App.css';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Layout({ children }) {
  const { pathname } = useLocation();
  const isAdminPage = pathname.startsWith('/admin');
  const isAuthPage = pathname === '/forgot-password' || pathname === '/reset-password';
  
  if (isAdminPage || isAuthPage) {
    return <>{children}</>;
  }
  
  return (
    <div className="App min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function AppContent() {
  const { initSession, fetchCart } = useCart();
  const [siteStatus, setSiteStatus] = useState('open');
  const [statusLoaded, setStatusLoaded] = useState(false);
  const location = window.location.pathname;
  const isAdmin = location.startsWith('/admin');

  useEffect(() => {
    const fetchStatus = async () => {
      try {
        const res = await axios.get(`${API}/site-status`);
        setSiteStatus(res.data.status || 'open');
      } catch {
        setSiteStatus('open');
      } finally {
        setStatusLoaded(true);
      }
    };
    fetchStatus();
  }, []);

  useEffect(() => {
    if (!statusLoaded) return;
    if (siteStatus !== 'open' && !isAdmin) return;
    const loadCart = async () => {
      initSession();
      await fetchCart();
    };
    loadCart();
  }, [statusLoaded, siteStatus]);

  // Show nothing while checking status (prevents flash)
  if (!statusLoaded && !isAdmin) {
    return null;
  }

  // Allow admin access regardless of site status
  if (!isAdmin) {
    if (siteStatus === 'maintenance') return <MaintenancePage />;
    if (siteStatus === 'closed') return <ClosedForTodayPage />;
  }

  return (
    <>
      <ScrollToTop />
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/current-deals" element={<CurrentDeals />} />
          <Route path="/tank-tailored-bundle" element={<TankTailoredBundle />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/product/:productId" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<Checkout />} />
          <Route path="/order-confirmation/:orderId" element={<OrderConfirmation />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
        </Routes>
      </Layout>
      <Toaster position="top-right" richColors />
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
